import { Injectable, computed, inject, signal } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpEventType,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { firstValueFrom, timeout } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  CambioCampoSplit,
  CatalogosSplitsMml,
  ColaboradorSplitEntity,
  ContratoSplitCompleto,
  ContratoSplitEntity,
  EventoTrazabilidadEntity,
  ParticipanteDirectorioEntity,
  VersionSplitEntity,
  normalizarNumeroDocumento,
  normalizarRolSplitOficial,
  normalizarTipoDocumento
} from '../core/models/splits.model';

export interface EstadoSplitsPersistido {
  contratos: ContratoSplitEntity[];
  colaboradores: ColaboradorSplitEntity[];
  directorioParticipantes: ParticipanteDirectorioEntity[];
  versionesPorContrato: Record<string, VersionSplitEntity[]>;
  trazabilidadPorContrato: Record<string, EventoTrazabilidadEntity[]>;
  contratosCompletosPorId: Record<string, ContratoSplitCompleto>;
  catalogos?: CatalogosSplitsMml;
  actualizadoEnIso?: string;
}

export interface SnapshotVersionPendienteSplits {
  id: string;
  contratoId: string;
  markdown: string;
  autor: string;
  motivo: string;
  archivoUrl?: string;
  storageKey?: string;
  nombreArchivo?: string;
  tamanoBytes?: number;
  idArchivoCola?: string;
  pendienteSubidaS3?: boolean;
  cambios?: CambioCampoSplit[];
  creadoEn: string;
}

export interface ArchivoS3PendienteEnCola {
  id: string;
  contratoId: string;
  carpeta: 'mml-splits/firmados' | 'mml-splits/versiones';
  nombreArchivo: string;
  tipoMime: string;
  tamanoBytes: number;
  blob: Blob;
  creadoEnIso: string;
}

export interface ProgresoSubidaDocumentoS3 {
  activo: boolean;
  porcentaje: number;
  nombreArchivo: string;
  tamanoBytes: number;
  etapa: 'preparando' | 'subiendo' | 'en_cola' | 'completado' | 'error';
  mensaje: string;
}

export interface ResultadoSubidaDocumentoS3 {
  subido: boolean;
  url: string;
  storageKey?: string;
  pendienteSubidaS3: boolean;
  idArchivoCola?: string;
}

export interface OperacionesSincronizacionSplits {
  contratosUpsert: Record<string, ContratoSplitCompleto>;
  contratosEliminarIds: string[];
  contratosRestaurarIds: string[];
  versionesCrear: SnapshotVersionPendienteSplits[];
  eventosTrazabilidad: EventoTrazabilidadEntity[];
  directorioUpsert: Record<string, ParticipanteDirectorioEntity>;
  directorioEliminarIds: string[];
}

export interface ItemColaSplitsSincronizacion {
  id: string;
  creadoEnIso: string;
  actualizadoEnIso: string;
  intentos: number;
  ultimoIntentoIso?: string;
  ultimoError?: string;
  operaciones: OperacionesSincronizacionSplits;
}

export interface ResumenItemColaSplitsUi {
  id: string;
  etiqueta: string;
  detalleOperaciones: string[];
  contratosIds: string[];
  intentos: number;
  creadoEnIso: string;
  actualizadoEnIso: string;
  ultimoIntentoIso?: string;
  ultimoError?: string;
}

export interface EventoSincronizacionColaSplits {
  id: string;
  timestamp: string;
  tipo:
    | 'ENCOLADO_OFFLINE'
    | 'ENCOLADO_ONLINE'
    | 'SINCRONIZADO_OK'
    | 'ERROR_REINTENTO'
    | 'MODO_OFFLINE'
    | 'CONEXION_RESTABLECIDA';
  titulo: string;
  detalle: string;
  contratoId?: string;
}

export type EstadoSincronizacionSplitsUi =
  | 'sincronizado'
  | 'sincronizando'
  | 'pendiente'
  | 'offline'
  | 'offline_con_cola'
  | 'error';

const IDB_NAME = 'mj_splits_mml_idb_v2';
const IDB_VERSION = 1;
const STORE_SNAPSHOT = 'snapshot';
const STORE_OUTBOX = 'outbox';
const STORE_ARCHIVOS_S3 = 'archivos_s3_pendientes';
const KEY_SNAPSHOT_MAIN = 'estado_principal_splits_v2';
const KEY_OUTBOX_MAIN = 'cola_pendiente_splits_v2';
const LS_OUTBOX_KEY = 'mj_splits_mml_outbox_v2';
const LS_TRACE_SYNC_KEY = 'mj_splits_mml_sync_trace_v2';

const REQUEST_TIMEOUT_MS = 20_000;
const UPLOAD_S3_TIMEOUT_MS = 45_000;
const BASE_RETRY_MS = 4_000;
const MAX_RETRY_MS = 60_000;

export function crearOperacionesSplitsVacias(): OperacionesSincronizacionSplits {
  return {
    contratosUpsert: {},
    contratosEliminarIds: [],
    contratosRestaurarIds: [],
    versionesCrear: [],
    eventosTrazabilidad: [],
    directorioUpsert: {},
    directorioEliminarIds: []
  };
}

export function esOperacionesSplitsVacias(op: OperacionesSincronizacionSplits): boolean {
  return (
    Object.keys(op.contratosUpsert || {}).length === 0 &&
    (op.contratosEliminarIds?.length || 0) === 0 &&
    (op.contratosRestaurarIds?.length || 0) === 0 &&
    (op.versionesCrear?.length || 0) === 0 &&
    (op.eventosTrazabilidad?.length || 0) === 0 &&
    Object.keys(op.directorioUpsert || {}).length === 0 &&
    (op.directorioEliminarIds?.length || 0) === 0
  );
}

@Injectable({
  providedIn: 'root'
})
export class ColaSincronizacionSplitsService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = environment.URL_SERVER.endsWith('/')
    ? `${environment.URL_SERVER}mml-splits`
    : `${environment.URL_SERVER}/mml-splits`;
  private readonly uploadFileUrl = environment.URL_SERVER.endsWith('/')
    ? `${environment.URL_SERVER}upload-file`
    : `${environment.URL_SERVER}/upload-file`;
  private readonly s3BaseUrl = environment.URL_S3 || 'https://d3q0kt5yih1b7n.cloudfront.net/';
  private readonly S3_BUCKET = 'medellinjoven-production';

  private dbPromise: Promise<IDBDatabase | null> | null = null;
  private retryTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private countdownIntervalId: ReturnType<typeof setInterval> | null = null;
  private procesando = false;
  private listenersPostSync: Array<() => void> = [];

  // Estado reactivo para la UI y observabilidad
  private readonly colaInterna = signal<ItemColaSplitsSincronizacion[]>([]);
  readonly enLinea = signal<boolean>(typeof navigator !== 'undefined' ? navigator.onLine : true);
  readonly sincronizando = signal<boolean>(false);
  readonly ultimoError = signal<string | null>(null);
  readonly segundosParaReintento = signal<number>(0);
  readonly ultimaSincronizacionExitosaIso = signal<string | null>(null);
  readonly historialSincronizacion = signal<EventoSincronizacionColaSplits[]>([]);

  // Progreso en tiempo real de subida de documentos a S3 (inspirado en Asistencia MML)
  readonly progresoSubidaS3 = signal<ProgresoSubidaDocumentoS3 | null>(null);

  readonly cola = this.colaInterna.asReadonly();
  readonly pendientesCount = computed(() => this.colaInterna().length);

  readonly totalOperacionesPendientes = computed(() => {
    let total = 0;
    for (const item of this.colaInterna()) {
      const op = item.operaciones;
      total +=
        Object.keys(op.contratosUpsert || {}).length +
        (op.contratosEliminarIds?.length || 0) +
        (op.contratosRestaurarIds?.length || 0) +
        (op.versionesCrear?.length || 0) +
        (op.eventosTrazabilidad?.length || 0) +
        Object.keys(op.directorioUpsert || {}).length +
        (op.directorioEliminarIds?.length || 0);
    }
    return total;
  });

  readonly contratosPendientesIds = computed<Set<string>>(() => {
    const ids = new Set<string>();
    for (const item of this.colaInterna()) {
      const op = item.operaciones;
      for (const id of Object.keys(op.contratosUpsert || {})) {
        ids.add(id);
      }
      for (const id of op.contratosEliminarIds || []) {
        ids.add(id);
      }
      for (const id of op.contratosRestaurarIds || []) {
        ids.add(id);
      }
      for (const v of op.versionesCrear || []) {
        if (v.contratoId) ids.add(v.contratoId);
      }
      for (const ev of op.eventosTrazabilidad || []) {
        if (ev.contratoId) ids.add(ev.contratoId);
      }
    }
    return ids;
  });

  readonly itemsEnColaUi = computed<ResumenItemColaSplitsUi[]>(() =>
    this.colaInterna().map((item) => {
      const detalles = this.construirDetalleOperacionesItem(item.operaciones);
      const contratosIds = Array.from(
        new Set([
          ...Object.keys(item.operaciones.contratosUpsert || {}),
          ...(item.operaciones.contratosEliminarIds || []),
          ...(item.operaciones.contratosRestaurarIds || [])
        ])
      );
      return {
        id: item.id,
        etiqueta: this.construirEtiquetaItem(item.operaciones),
        detalleOperaciones: detalles,
        contratosIds,
        intentos: item.intentos,
        creadoEnIso: item.creadoEnIso,
        actualizadoEnIso: item.actualizadoEnIso,
        ultimoIntentoIso: item.ultimoIntentoIso,
        ultimoError: item.ultimoError
      };
    })
  );

  readonly estadoUi = computed<EstadoSincronizacionSplitsUi>(() => {
    const online = this.enLinea();
    const pendientes = this.pendientesCount();
    const sync = this.sincronizando();
    const err = this.ultimoError();

    if (!online) {
      return pendientes > 0 ? 'offline_con_cola' : 'offline';
    }
    if (sync) {
      return 'sincronizando';
    }
    if (pendientes > 0 && err) {
      return 'error';
    }
    if (pendientes > 0) {
      return 'pendiente';
    }
    return 'sincronizado';
  });

  constructor() {
    this.restaurarHistorialTrazabilidadLocal();
    this.restaurarColaDesdeLocalStorage();
    this.configurarListenersConectividad();
    void this.inicializarDesdeIndexedDb();
  }

  onPostSincronizacion(cb: () => void): void {
    this.listenersPostSync.push(cb);
  }

  private notificarPostSincronizacion(): void {
    for (const cb of this.listenersPostSync) {
      try {
        cb();
      } catch {
        // Ignorar errores de callback
      }
    }
  }

  tieneCambiosPendientes(): boolean {
    return this.colaInterna().length > 0;
  }

  tieneContratoPendiente(contratoId: string): boolean {
    if (!contratoId) return false;
    return this.contratosPendientesIds().has(contratoId);
  }

  // =========================================================================
  // SUBIDA DE DOCUMENTOS FIRMADOS Y VERSIONES A AWS S3 CON COLA OFFLINE
  // =========================================================================
  async subirDocumentoAS3ConCola(params: {
    contratoId: string;
    blob: Blob;
    nombreArchivo: string;
    tipoMime?: string;
    carpeta: 'mml-splits/firmados' | 'mml-splits/versiones';
    mostrarProgresoUi?: boolean;
  }): Promise<ResultadoSubidaDocumentoS3> {
    const {
      contratoId,
      blob,
      nombreArchivo,
      tipoMime = blob.type || 'application/pdf',
      carpeta,
      mostrarProgresoUi = true
    } = params;

    const nombreSanitizado = (nombreArchivo || 'Split_Sheet_EPG.pdf')
      .replace(/[^a-zA-Z0-9._-]/g, '_')
      .replace(/_+/g, '_');
    const nombreUnico = `${contratoId}_${Date.now()}_${nombreSanitizado}`;
    const idArchivoCola = `s3-split-${contratoId}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

    const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
    this.enLinea.set(online);

    if (online) {
      try {
        if (mostrarProgresoUi) {
          this.progresoSubidaS3.set({
            activo: true,
            porcentaje: 12,
            nombreArchivo,
            tamanoBytes: blob.size,
            etapa: 'subiendo',
            mensaje: `Subiendo "${nombreArchivo}" a custodia digital...`
          });
        }

        const subido = await this.ejecutarUploadS3ConProgreso(
          blob,
          nombreUnico,
          carpeta,
          mostrarProgresoUi ? nombreArchivo : undefined
        );

        if (mostrarProgresoUi) {
          this.progresoSubidaS3.set({
            activo: true,
            porcentaje: 100,
            nombreArchivo,
            tamanoBytes: blob.size,
            etapa: 'completado',
            mensaje: `Documento resguardado correctamente.`
          });
          setTimeout(() => {
            if (this.progresoSubidaS3()?.etapa === 'completado') {
              this.progresoSubidaS3.set(null);
            }
          }, 1800);
        }

        return {
          subido: true,
          url: subido.urlCompleta,
          storageKey: subido.storageKey,
          pendienteSubidaS3: false
        };
      } catch {
        // Si falla la red durante la subida, cae de forma transparente a la cola en IndexedDB
      }
    }

    // Resguardar el archivo binario en IndexedDB para subirlo a S3 en cuanto regrese el internet
    await this.guardarArchivoS3PendienteEnIdb({
      id: idArchivoCola,
      contratoId,
      carpeta,
      nombreArchivo: nombreUnico,
      tipoMime,
      tamanoBytes: blob.size,
      blob,
      creadoEnIso: new Date().toISOString()
    });

    const blobUrlTemporal =
      typeof URL !== 'undefined' && URL.createObjectURL ? URL.createObjectURL(blob) : '';

    if (mostrarProgresoUi) {
      this.progresoSubidaS3.set({
        activo: true,
        porcentaje: 100,
        nombreArchivo,
        tamanoBytes: blob.size,
        etapa: 'en_cola',
        mensaje: `Guardado en cola en este dispositivo. Se subirá automáticamente cuando vuelva el internet.`
      });
      setTimeout(() => {
        if (this.progresoSubidaS3()?.etapa === 'en_cola') {
          this.progresoSubidaS3.set(null);
        }
      }, 2600);
    }

    return {
      subido: false,
      url: blobUrlTemporal,
      pendienteSubidaS3: true,
      idArchivoCola
    };
  }

  private ejecutarUploadS3ConProgreso(
    blob: Blob,
    nombreUnico: string,
    carpeta: 'mml-splits/firmados' | 'mml-splits/versiones',
    nombreVisibleProgreso?: string
  ): Promise<{ urlCompleta: string; storageKey: string }> {
    return new Promise<{ urlCompleta: string; storageKey: string }>((resolve, reject) => {
      const formData = new FormData();
      formData.append('bucket', this.S3_BUCKET);
      formData.append('folder', carpeta);
      formData.append('file', blob, nombreUnico);

      const req = new HttpRequest('POST', this.uploadFileUrl, formData, {
        reportProgress: true
      });

      const sub = this.http
        .request<{ success?: boolean; url?: string; message?: string }>(req)
        .pipe(timeout(UPLOAD_S3_TIMEOUT_MS))
        .subscribe({
          next: (event) => {
            if (event.type === HttpEventType.UploadProgress && nombreVisibleProgreso) {
              const total = event.total || blob.size || 1;
              const pct = Math.min(95, Math.max(15, Math.round((event.loaded / total) * 90)));
              this.progresoSubidaS3.set({
                activo: true,
                porcentaje: pct,
                nombreArchivo: nombreVisibleProgreso,
                tamanoBytes: blob.size,
                etapa: 'subiendo',
                mensaje: `Subiendo "${nombreVisibleProgreso}" (${pct}%)...`
              });
            } else if (event instanceof HttpResponse) {
              const body = event.body;
              if (body?.success && body.url) {
                const storageKey = String(body.url).replace(/^\//, '');
                const urlCompleta = storageKey.startsWith('http')
                  ? storageKey
                  : `${this.s3BaseUrl.replace(/\/$/, '')}/${storageKey}`;
                sub.unsubscribe();
                resolve({ urlCompleta, storageKey });
              } else {
                sub.unsubscribe();
                reject(new Error(body?.message || 'El servidor no devolvió la ruta del documento.'));
              }
            }
          },
          error: (err) => {
            sub.unsubscribe();
            reject(err);
          }
        });
    });
  }

  private async guardarArchivoS3PendienteEnIdb(item: ArchivoS3PendienteEnCola): Promise<void> {
    const db = await this.abrirBaseDatos();
    if (!db || !db.objectStoreNames.contains(STORE_ARCHIVOS_S3)) return;
    try {
      await new Promise<void>((resolve) => {
        const tx = db.transaction(STORE_ARCHIVOS_S3, 'readwrite');
        const store = tx.objectStore(STORE_ARCHIVOS_S3);
        store.put(item);
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      });
    } catch {
      // Silencioso si IndexedDB está restringido
    }
  }

  async obtenerArchivoS3PendientePorId(idArchivoCola: string): Promise<ArchivoS3PendienteEnCola | null> {
    if (!idArchivoCola) return null;
    const db = await this.abrirBaseDatos();
    if (!db || !db.objectStoreNames.contains(STORE_ARCHIVOS_S3)) return null;
    try {
      return await new Promise<ArchivoS3PendienteEnCola | null>((resolve) => {
        const tx = db.transaction(STORE_ARCHIVOS_S3, 'readonly');
        const store = tx.objectStore(STORE_ARCHIVOS_S3);
        const req = store.get(idArchivoCola);
        req.onsuccess = () => resolve((req.result as ArchivoS3PendienteEnCola) ?? null);
        req.onerror = () => resolve(null);
      });
    } catch {
      return null;
    }
  }

  private async eliminarArchivoS3PendienteDeIdb(idArchivoCola: string): Promise<void> {
    if (!idArchivoCola) return;
    const db = await this.abrirBaseDatos();
    if (!db || !db.objectStoreNames.contains(STORE_ARCHIVOS_S3)) return;
    try {
      await new Promise<void>((resolve) => {
        const tx = db.transaction(STORE_ARCHIVOS_S3, 'readwrite');
        const store = tx.objectStore(STORE_ARCHIVOS_S3);
        store.delete(idArchivoCola);
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      });
    } catch {
      // Silencioso
    }
  }

  async obtenerBlobUrlLocalPendienteS3(idArchivoCola?: string): Promise<string | null> {
    if (!idArchivoCola) return null;
    const pendiente = await this.obtenerArchivoS3PendientePorId(idArchivoCola);
    if (!pendiente || !pendiente.blob) return null;
    return URL.createObjectURL(pendiente.blob);
  }

  private async resolverSubidaS3PendientePorId(
    idArchivoCola?: string,
    dataBase64Fallback?: string,
    contratoIdFallback?: string,
    nombreFallback?: string,
    carpetaFallback: 'mml-splits/firmados' | 'mml-splits/versiones' = 'mml-splits/firmados'
  ): Promise<{ urlCompleta: string; storageKey: string } | null> {
    if (idArchivoCola) {
      const pendiente = await this.obtenerArchivoS3PendientePorId(idArchivoCola);
      if (pendiente && pendiente.blob) {
        const res = await this.ejecutarUploadS3ConProgreso(
          pendiente.blob,
          pendiente.nombreArchivo,
          pendiente.carpeta
        );
        await this.eliminarArchivoS3PendienteDeIdb(idArchivoCola);
        return res;
      }
    }

    if (dataBase64Fallback && dataBase64Fallback.startsWith('data:')) {
      const blob = this.base64ABlob(dataBase64Fallback);
      if (blob) {
        const safeName = (nombreFallback || 'Split_Sheet_EPG.pdf')
          .replace(/[^a-zA-Z0-9._-]/g, '_')
          .replace(/_+/g, '_');
        const nombreUnico = `${contratoIdFallback || 'split'}_${Date.now()}_${safeName}`;
        return await this.ejecutarUploadS3ConProgreso(blob, nombreUnico, carpetaFallback);
      }
    }

    return null;
  }

  private base64ABlob(dataUri: string): Blob | null {
    try {
      const parts = dataUri.split(';base64,');
      const mime = parts[0].replace('data:', '') || 'application/pdf';
      const raw = atob(parts[1] || parts[0]);
      const arr = new Uint8Array(raw.length);
      for (let i = 0; i < raw.length; i++) {
        arr[i] = raw.charCodeAt(i);
      }
      return new Blob([arr], { type: mime });
    } catch {
      return null;
    }
  }

  // =========================================================================
  // TRAZABILIDAD DE COLA Y AUDITORÍA DE SINCRONIZACIÓN
  // =========================================================================
  registrarTrazaCola(
    tipo: EventoSincronizacionColaSplits['tipo'],
    titulo: string,
    detalle: string,
    contratoId?: string
  ): void {
    const nuevo: EventoSincronizacionColaSplits = {
      id: `sync-split-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      timestamp: new Date().toISOString(),
      tipo,
      titulo,
      detalle,
      contratoId
    };
    const lista = [nuevo, ...this.historialSincronizacion()].slice(0, 60);
    this.historialSincronizacion.set(lista);
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        window.localStorage.setItem(LS_TRACE_SYNC_KEY, JSON.stringify(lista));
      } catch {
        // Ignorar cuota de localStorage
      }
    }
  }

  private restaurarHistorialTrazabilidadLocal(): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      const raw = window.localStorage.getItem(LS_TRACE_SYNC_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        this.historialSincronizacion.set(parsed.slice(0, 60));
      }
    } catch {
      // Ignorar errores de parseo
    }
  }

  // =========================================================================
  // DETECCIÓN DE RED Y EVENTOS DEL NAVEGADOR (SIN SPAM CUANDO NO HAY INTERNET)
  // =========================================================================
  private configurarListenersConectividad(): void {
    if (typeof window === 'undefined') return;

    window.addEventListener('online', () => {
      this.enLinea.set(true);
      this.ultimoError.set(null);
      this.limpiarTimersReintento();
      if (this.tieneCambiosPendientes()) {
        this.registrarTrazaCola(
          'CONEXION_RESTABLECIDA',
          'Conexión a internet restablecida',
          `Subiendo automáticamente ${this.pendientesCount()} cambio(s) pendiente(s).`
        );
      }
      void this.procesarCola(true);
    });

    window.addEventListener('offline', () => {
      this.enLinea.set(false);
      this.limpiarTimersReintento();
      this.registrarTrazaCola(
        'MODO_OFFLINE',
        'Modo sin internet activado',
        'Tus cambios se guardarán en cola en este dispositivo y se subirán automáticamente cuando vuelva el internet.'
      );
    });

    window.addEventListener('focus', () => {
      const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
      this.enLinea.set(online);
      if (online && this.tieneCambiosPendientes() && !this.procesando) {
        void this.procesarCola(false);
      }
    });

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
          this.enLinea.set(online);
          if (online && this.tieneCambiosPendientes() && !this.procesando) {
            void this.procesarCola(false);
          }
        }
      });
    }
  }

  // =========================================================================
  // INDEXEDDB: SNAPSHOT OFFLINE COMPLETO + OUTBOX + ARCHIVOS S3 PENDIENTES
  // =========================================================================
  private abrirBaseDatos(): Promise<IDBDatabase | null> {
    if (typeof window === 'undefined' || !('indexedDB' in window)) {
      return Promise.resolve(null);
    }
    if (this.dbPromise) return this.dbPromise;

    this.dbPromise = new Promise<IDBDatabase | null>((resolve) => {
      try {
        const req = window.indexedDB.open(IDB_NAME, IDB_VERSION);
        req.onupgradeneeded = () => {
          const db = req.result;
          if (!db.objectStoreNames.contains(STORE_SNAPSHOT)) {
            db.createObjectStore(STORE_SNAPSHOT);
          }
          if (!db.objectStoreNames.contains(STORE_OUTBOX)) {
            db.createObjectStore(STORE_OUTBOX);
          }
          if (!db.objectStoreNames.contains(STORE_ARCHIVOS_S3)) {
            db.createObjectStore(STORE_ARCHIVOS_S3, { keyPath: 'id' });
          }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(null);
      } catch {
        resolve(null);
      }
    });

    return this.dbPromise;
  }

  private async inicializarDesdeIndexedDb(): Promise<void> {
    const db = await this.abrirBaseDatos();
    if (!db) {
      if (this.enLinea() && this.tieneCambiosPendientes()) {
        void this.procesarCola(true);
      }
      return;
    }

    try {
      const colaIdb = await new Promise<ItemColaSplitsSincronizacion[] | null>((resolve) => {
        const tx = db.transaction(STORE_OUTBOX, 'readonly');
        const store = tx.objectStore(STORE_OUTBOX);
        const req = store.get(KEY_OUTBOX_MAIN);
        req.onsuccess = () =>
          resolve(Array.isArray(req.result) ? (req.result as ItemColaSplitsSincronizacion[]) : null);
        req.onerror = () => resolve(null);
      });

      if (colaIdb && colaIdb.length > 0) {
        const combinada = this.combinarColas(this.colaInterna(), colaIdb);
        this.colaInterna.set(combinada);
        this.guardarColaEnLocalStorage(combinada);
      } else if (this.colaInterna().length > 0) {
        await this.guardarColaEnIndexedDb(this.colaInterna());
      }
    } catch {
      // Continuar con copia en memoria
    }

    if (this.enLinea() && this.tieneCambiosPendientes()) {
      void this.procesarCola(true);
    }
  }

  async guardarSnapshotOffline(estado: EstadoSplitsPersistido): Promise<void> {
    const db = await this.abrirBaseDatos();
    if (!db) return;
    try {
      await new Promise<void>((resolve) => {
        const tx = db.transaction(STORE_SNAPSHOT, 'readwrite');
        const store = tx.objectStore(STORE_SNAPSHOT);
        store.put(
          {
            ...estado,
            actualizadoEnIso: new Date().toISOString()
          },
          KEY_SNAPSHOT_MAIN
        );
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      });
    } catch {
      // Silencioso si IndexedDB está restringido
    }
  }

  async cargarSnapshotOffline(): Promise<EstadoSplitsPersistido | null> {
    const db = await this.abrirBaseDatos();
    if (!db) return null;
    try {
      return await new Promise<EstadoSplitsPersistido | null>((resolve) => {
        const tx = db.transaction(STORE_SNAPSHOT, 'readonly');
        const store = tx.objectStore(STORE_SNAPSHOT);
        const req = store.get(KEY_SNAPSHOT_MAIN);
        req.onsuccess = () => resolve((req.result as EstadoSplitsPersistido) ?? null);
        req.onerror = () => resolve(null);
      });
    } catch {
      return null;
    }
  }

  private restaurarColaDesdeLocalStorage(): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      const raw = window.localStorage.getItem(LS_OUTBOX_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const validos = parsed.filter(
          (it: ItemColaSplitsSincronizacion) =>
            it && it.id && it.operaciones && !esOperacionesSplitsVacias(it.operaciones)
        );
        this.colaInterna.set(validos);
      }
    } catch {
      // Ignorar errores de parseo
    }
  }

  private guardarColaEnLocalStorage(cola: ItemColaSplitsSincronizacion[]): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      if (cola.length === 0) {
        window.localStorage.removeItem(LS_OUTBOX_KEY);
      } else {
        window.localStorage.setItem(LS_OUTBOX_KEY, JSON.stringify(cola));
      }
    } catch {
      // Si el payload excede la cuota de localStorage, IndexedDB conserva el payload íntegro.
    }
  }

  private async guardarColaEnIndexedDb(cola: ItemColaSplitsSincronizacion[]): Promise<void> {
    const db = await this.abrirBaseDatos();
    if (!db) return;
    try {
      await new Promise<void>((resolve) => {
        const tx = db.transaction(STORE_OUTBOX, 'readwrite');
        const store = tx.objectStore(STORE_OUTBOX);
        if (cola.length === 0) {
          store.delete(KEY_OUTBOX_MAIN);
        } else {
          store.put(cola, KEY_OUTBOX_MAIN);
        }
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      });
    } catch {
      // Ignorar errores de escritura en IndexedDB
    }
  }

  private persistirCola(cola: ItemColaSplitsSincronizacion[]): void {
    this.colaInterna.set(cola);
    this.guardarColaEnLocalStorage(cola);
    void this.guardarColaEnIndexedDb(cola);
  }

  private combinarColas(
    actual: ItemColaSplitsSincronizacion[],
    desdeIdb: ItemColaSplitsSincronizacion[]
  ): ItemColaSplitsSincronizacion[] {
    const mapa = new Map<string, ItemColaSplitsSincronizacion>();
    for (const item of desdeIdb) {
      if (item?.id && item.operaciones && !esOperacionesSplitsVacias(item.operaciones)) {
        mapa.set(item.id, item);
      }
    }
    for (const item of actual) {
      if (item?.id && item.operaciones && !esOperacionesSplitsVacias(item.operaciones)) {
        const existente = mapa.get(item.id);
        if (!existente || (item.actualizadoEnIso || '') > (existente.actualizadoEnIso || '')) {
          mapa.set(item.id, item);
        }
      }
    }
    return Array.from(mapa.values()).sort((a, b) => a.creadoEnIso.localeCompare(b.creadoEnIso));
  }

  // =========================================================================
  // ENCOLADO CON FUSIÓN INTELIGENTE (COALESCING) POR SPLIT SHEET / ENTIDAD
  // =========================================================================
  encolarGuardarContrato(agregado: ContratoSplitCompleto): void {
    const clon = this.clonarProfundo(agregado);
    const id = clon.contrato.id;
    const ops = crearOperacionesSplitsVacias();
    ops.contratosUpsert[id] = clon;

    const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
    const tituloObra = clon.contrato.tituloObra?.trim() || clon.contrato.codigoSesion || id;
    this.registrarTrazaCola(
      online ? 'ENCOLADO_ONLINE' : 'ENCOLADO_OFFLINE',
      online ? `Split Sheet en cola de subida` : `Guardado en cola (sin internet)`,
      `Split Sheet "${tituloObra}" guardado en cola para subirse automáticamente.`,
      id
    );

    this.encolarOperaciones(ops);
  }

  encolarEliminarContrato(contratoId: string, tituloObra?: string): void {
    const ops = crearOperacionesSplitsVacias();
    ops.contratosEliminarIds.push(contratoId);

    const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
    this.registrarTrazaCola(
      online ? 'ENCOLADO_ONLINE' : 'ENCOLADO_OFFLINE',
      `Archivo de Split Sheet en cola`,
      `Solicitud para archivar "${tituloObra || contratoId}" guardada en cola.`,
      contratoId
    );

    this.encolarOperaciones(ops);
  }

  encolarRestaurarContrato(contratoId: string): void {
    const ops = crearOperacionesSplitsVacias();
    ops.contratosRestaurarIds.push(contratoId);
    this.encolarOperaciones(ops);
  }

  encolarVersionSnapshot(version: SnapshotVersionPendienteSplits): void {
    const ops = crearOperacionesSplitsVacias();
    ops.versionesCrear.push(this.clonarProfundo(version));
    this.encolarOperaciones(ops);
  }

  encolarEventoTrazabilidad(evento: EventoTrazabilidadEntity): void {
    const ops = crearOperacionesSplitsVacias();
    ops.eventosTrazabilidad.push(this.clonarProfundo(evento));
    this.encolarOperaciones(ops);
  }

  encolarUpsertDirectorio(items: ParticipanteDirectorioEntity[]): void {
    if (!items || items.length === 0) return;
    const ops = crearOperacionesSplitsVacias();
    for (const item of items) {
      const docNorm = normalizarNumeroDocumento(item.numeroDocumento || item.id);
      if (!docNorm) continue;
      ops.directorioUpsert[docNorm] = this.clonarProfundo({
        ...item,
        id: docNorm,
        numeroDocumento: docNorm
      });
    }
    this.encolarOperaciones(ops);
  }

  encolarEliminarDirectorio(id: string): void {
    const docNorm = normalizarNumeroDocumento(id);
    if (!docNorm) return;
    const ops = crearOperacionesSplitsVacias();
    ops.directorioEliminarIds.push(docNorm);
    this.encolarOperaciones(ops);
  }

  private encolarOperaciones(nuevas: OperacionesSincronizacionSplits): void {
    if (esOperacionesSplitsVacias(nuevas)) return;

    const ahoraIso = new Date().toISOString();
    const colaActual = [...this.colaInterna()];

    if (colaActual.length > 0 && (!this.procesando || colaActual.length > 1)) {
      const idxUltimo = colaActual.length - 1;
      const ultimo = colaActual[idxUltimo];
      const fusionadas = this.fusionarOperaciones(ultimo.operaciones, nuevas);
      colaActual[idxUltimo] = {
        ...ultimo,
        actualizadoEnIso: ahoraIso,
        operaciones: fusionadas
      };
      this.persistirCola(colaActual);
    } else {
      const nuevoItem: ItemColaSplitsSincronizacion = {
        id: `outbox-splits-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        creadoEnIso: ahoraIso,
        actualizadoEnIso: ahoraIso,
        intentos: 0,
        operaciones: this.clonarProfundo(nuevas)
      };
      colaActual.push(nuevoItem);
      this.persistirCola(colaActual);
    }

    const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
    this.enLinea.set(online);
    if (online) {
      void this.procesarCola(false);
    }
  }

  private fusionarOperaciones(
    base: OperacionesSincronizacionSplits,
    entrante: OperacionesSincronizacionSplits
  ): OperacionesSincronizacionSplits {
    const res: OperacionesSincronizacionSplits = {
      contratosUpsert: { ...(base.contratosUpsert || {}) },
      contratosEliminarIds: [...(base.contratosEliminarIds || [])],
      contratosRestaurarIds: [...(base.contratosRestaurarIds || [])],
      versionesCrear: [...(base.versionesCrear || [])],
      eventosTrazabilidad: [...(base.eventosTrazabilidad || [])],
      directorioUpsert: { ...(base.directorioUpsert || {}) },
      directorioEliminarIds: [...(base.directorioEliminarIds || [])]
    };

    for (const [id, contratoCompleto] of Object.entries(entrante.contratosUpsert || {})) {
      const previo = res.contratosUpsert[id];
      const eventosCombinados = this.combinarEventosTrazabilidad(
        previo?.eventosTrazabilidad || [],
        contratoCompleto.eventosTrazabilidad || []
      );
      res.contratosUpsert[id] = {
        ...this.clonarProfundo(contratoCompleto),
        eventosTrazabilidad: eventosCombinados
      };
      res.contratosEliminarIds = res.contratosEliminarIds.filter((x) => x !== id);
    }

    for (const id of entrante.contratosEliminarIds || []) {
      if (res.contratosUpsert[id]) {
        res.contratosUpsert[id] = {
          ...res.contratosUpsert[id],
          contrato: {
            ...res.contratosUpsert[id].contrato,
            eliminado: true,
            actualizadoEn: new Date().toISOString()
          }
        };
      }
      if (!res.contratosEliminarIds.includes(id)) {
        res.contratosEliminarIds.push(id);
      }
      res.contratosRestaurarIds = res.contratosRestaurarIds.filter((x) => x !== id);
    }

    for (const id of entrante.contratosRestaurarIds || []) {
      if (res.contratosUpsert[id]) {
        res.contratosUpsert[id] = {
          ...res.contratosUpsert[id],
          contrato: {
            ...res.contratosUpsert[id].contrato,
            eliminado: false,
            actualizadoEn: new Date().toISOString()
          }
        };
      }
      if (!res.contratosRestaurarIds.includes(id)) {
        res.contratosRestaurarIds.push(id);
      }
      res.contratosEliminarIds = res.contratosEliminarIds.filter((x) => x !== id);
    }

    const versionesMap = new Map<string, SnapshotVersionPendienteSplits>();
    for (const v of res.versionesCrear) {
      versionesMap.set(v.id, v);
    }
    for (const v of entrante.versionesCrear || []) {
      versionesMap.set(v.id, this.clonarProfundo(v));
    }
    res.versionesCrear = Array.from(versionesMap.values());

    res.eventosTrazabilidad = this.combinarEventosTrazabilidad(
      res.eventosTrazabilidad,
      entrante.eventosTrazabilidad || []
    );

    for (const [doc, item] of Object.entries(entrante.directorioUpsert || {})) {
      res.directorioUpsert[doc] = this.clonarProfundo(item);
      res.directorioEliminarIds = res.directorioEliminarIds.filter((x) => x !== doc);
    }

    for (const doc of entrante.directorioEliminarIds || []) {
      delete res.directorioUpsert[doc];
      if (!res.directorioEliminarIds.includes(doc)) {
        res.directorioEliminarIds.push(doc);
      }
    }

    return res;
  }

  private combinarEventosTrazabilidad(
    listaA: EventoTrazabilidadEntity[],
    listaB: EventoTrazabilidadEntity[]
  ): EventoTrazabilidadEntity[] {
    const mapa = new Map<string, EventoTrazabilidadEntity>();
    for (const ev of listaA || []) {
      if (ev?.id) mapa.set(ev.id, ev);
    }
    for (const ev of listaB || []) {
      if (ev?.id) mapa.set(ev.id, this.clonarProfundo(ev));
    }
    return Array.from(mapa.values()).sort((a, b) =>
      (b.timestamp || '').localeCompare(a.timestamp || '')
    );
  }

  // =========================================================================
  // SUPERPOSICIÓN DE COLA PENDIENTE SOBRE ESTADO REMOTO (PROTECCIÓN LOCAL)
  // =========================================================================
  aplicarColaPendienteSobreEstado(estadoBase: EstadoSplitsPersistido): EstadoSplitsPersistido {
    const cola = this.colaInterna();
    if (cola.length === 0) return estadoBase;

    const contratosMap = new Map<string, ContratoSplitEntity>();
    for (const c of estadoBase.contratos || []) {
      contratosMap.set(c.id, { ...c });
    }

    const colabsPorContrato = new Map<string, ColaboradorSplitEntity[]>();
    for (const colab of estadoBase.colaboradores || []) {
      const lista = colabsPorContrato.get(colab.contratoId) || [];
      lista.push({ ...colab });
      colabsPorContrato.set(colab.contratoId, lista);
    }

    const directorioMap = new Map<string, ParticipanteDirectorioEntity>();
    for (const d of estadoBase.directorioParticipantes || []) {
      const docNorm = normalizarNumeroDocumento(d.numeroDocumento || d.id);
      if (docNorm) directorioMap.set(docNorm, { ...d });
    }

    const versionesPorContrato: Record<string, VersionSplitEntity[]> = {
      ...(estadoBase.versionesPorContrato || {})
    };
    const trazabilidadPorContrato: Record<string, EventoTrazabilidadEntity[]> = {
      ...(estadoBase.trazabilidadPorContrato || {})
    };
    const contratosCompletosPorId: Record<string, ContratoSplitCompleto> = {
      ...(estadoBase.contratosCompletosPorId || {})
    };

    for (const item of cola) {
      const op = item.operaciones;

      for (const [id, completo] of Object.entries(op.contratosUpsert || {})) {
        contratosMap.set(id, { ...completo.contrato });
        colabsPorContrato.set(
          id,
          (completo.colaboradores || []).filter((c) => !c.eliminado).map((c) => ({ ...c }))
        );
        contratosCompletosPorId[id] = this.clonarProfundo(completo);

        if (completo.eventosTrazabilidad && completo.eventosTrazabilidad.length > 0) {
          trazabilidadPorContrato[id] = this.combinarEventosTrazabilidad(
            trazabilidadPorContrato[id] || [],
            completo.eventosTrazabilidad
          );
        }

        for (const colab of completo.colaboradores || []) {
          if (colab.eliminado) continue;
          const docNorm = normalizarNumeroDocumento(colab.numeroDocumento);
          const nombreLimpio = (colab.nombreCompleto || '').trim();
          if (docNorm && docNorm.length >= 5 && nombreLimpio) {
            const prev = directorioMap.get(docNorm);
            directorioMap.set(docNorm, {
              id: docNorm,
              tipoDocumento: normalizarTipoDocumento(colab.tipoDocumento || prev?.tipoDocumento),
              numeroDocumento: docNorm,
              nombreCompleto: nombreLimpio,
              nombreArtistico: colab.nombreArtistico?.trim() || prev?.nombreArtistico || '',
              email: colab.email?.trim() || prev?.email || '',
              telefono: colab.telefono?.trim() || prev?.telefono || '',
              rolSugerido: normalizarRolSplitOficial(colab.rolContribucion || prev?.rolSugerido),
              aporteSugerido: colab.aporteCreativo?.trim() || prev?.aporteSugerido || '',
              ipi: colab.ipi?.trim() || prev?.ipi || '',
              obraReferenciaCsv: prev?.obraReferenciaCsv || completo.contrato.tituloObra || '',
              origen: prev?.origen || 'EXPEDIENTE',
              fechaImportacion: prev?.fechaImportacion || item.creadoEnIso,
              actualizadoEn: item.actualizadoEnIso
            });
          }
        }
      }

      for (const id of op.contratosEliminarIds || []) {
        const c = contratosMap.get(id);
        if (c) {
          contratosMap.set(id, { ...c, eliminado: true, actualizadoEn: item.actualizadoEnIso });
        }
        if (contratosCompletosPorId[id]) {
          contratosCompletosPorId[id] = {
            ...contratosCompletosPorId[id],
            contrato: {
              ...contratosCompletosPorId[id].contrato,
              eliminado: true,
              actualizadoEn: item.actualizadoEnIso
            }
          };
        }
      }

      for (const id of op.contratosRestaurarIds || []) {
        const c = contratosMap.get(id);
        if (c) {
          contratosMap.set(id, { ...c, eliminado: false, actualizadoEn: item.actualizadoEnIso });
        }
        if (contratosCompletosPorId[id]) {
          contratosCompletosPorId[id] = {
            ...contratosCompletosPorId[id],
            contrato: {
              ...contratosCompletosPorId[id].contrato,
              eliminado: false,
              actualizadoEn: item.actualizadoEnIso
            }
          };
        }
      }

      for (const v of op.versionesCrear || []) {
        const listaPrev = versionesPorContrato[v.contratoId] || [];
        if (!listaPrev.some((existing) => existing.id === v.id)) {
          const versionOptimista: VersionSplitEntity = {
            id: v.id,
            contratoId: v.contratoId,
            versionNumero: listaPrev.length + 1,
            markdownSnapshot: v.markdown,
            autorCambio: v.autor,
            motivo: v.motivo,
            archivoUrl: v.archivoUrl,
            storageKey: v.storageKey,
            nombreArchivo: v.nombreArchivo,
            tamanoBytes: v.tamanoBytes,
            pendienteSubidaS3: v.pendienteSubidaS3,
            cambios: v.cambios,
            creadoEn: v.creadoEn
          };
          versionesPorContrato[v.contratoId] = [versionOptimista, ...listaPrev];
        }
      }

      for (const ev of op.eventosTrazabilidad || []) {
        trazabilidadPorContrato[ev.contratoId] = this.combinarEventosTrazabilidad(
          trazabilidadPorContrato[ev.contratoId] || [],
          [ev]
        );
      }

      for (const [doc, p] of Object.entries(op.directorioUpsert || {})) {
        directorioMap.set(doc, { ...p });
      }

      for (const doc of op.directorioEliminarIds || []) {
        directorioMap.delete(doc);
      }
    }

    const todosColaboradores: ColaboradorSplitEntity[] = [];
    colabsPorContrato.forEach((lista) => {
      todosColaboradores.push(...lista);
    });

    return {
      contratos: Array.from(contratosMap.values()),
      colaboradores: todosColaboradores,
      directorioParticipantes: Array.from(directorioMap.values()),
      versionesPorContrato,
      trazabilidadPorContrato,
      contratosCompletosPorId,
      catalogos: estadoBase.catalogos,
      actualizadoEnIso: new Date().toISOString()
    };
  }

  obtenerContratoPendienteEnCola(contratoId: string): ContratoSplitCompleto | null {
    if (!contratoId) return null;
    const cola = this.colaInterna();
    let encontrado: ContratoSplitCompleto | null = null;
    for (const item of cola) {
      const c = item.operaciones.contratosUpsert?.[contratoId];
      if (c) {
        encontrado = this.clonarProfundo(c);
      }
    }
    return encontrado;
  }

  // =========================================================================
  // PROCESAMIENTO SECUENCIAL CON SUBIDA S3 PREVIA, CONFIRMACIÓN HTTP 200 Y BACKOFF
  // =========================================================================
  async reintentarAhora(): Promise<void> {
    this.limpiarTimersReintento();
    const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
    this.enLinea.set(online);
    if (!online) {
      this.ultimoError.set(
        'Sigues sin conexión a internet. Tus cambios están guardados en cola y se subirán automáticamente al recuperar la conexión.'
      );
      return;
    }
    await this.procesarCola(true);
  }

  async procesarCola(forzadoManual = false): Promise<void> {
    if (this.procesando) return;

    const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
    this.enLinea.set(online);
    if (!online) {
      this.limpiarTimersReintento();
      return;
    }

    if (this.colaInterna().length === 0) {
      this.ultimoError.set(null);
      this.limpiarTimersReintento();
      return;
    }

    if (forzadoManual) {
      this.limpiarTimersReintento();
    }

    this.procesando = true;
    this.sincronizando.set(true);

    try {
      while (this.colaInterna().length > 0) {
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
          this.enLinea.set(false);
          break;
        }

        const itemActual = this.colaInterna()[0];
        if (!itemActual || esOperacionesSplitsVacias(itemActual.operaciones)) {
          this.persistirCola(this.colaInterna().slice(1));
          continue;
        }

        try {
          await this.despacharOperacionesItemConConfirmacionParcial(itemActual.id);

          const colaRestante = this.colaInterna().filter((it) => it.id !== itemActual.id);
          this.persistirCola(colaRestante);
          this.ultimoError.set(null);
          this.ultimaSincronizacionExitosaIso.set(new Date().toISOString());

          this.registrarTrazaCola(
            'SINCRONIZADO_OK',
            'Cambios subidos correctamente',
            `Se subieron los cambios pendientes (${this.construirEtiquetaItem(itemActual.operaciones)}).`
          );

          this.notificarPostSincronizacion();
        } catch (err) {
          const mensajeError = this.formatearErrorHttp(err);
          const ahoraIso = new Date().toISOString();

          const colaActualizada = [...this.colaInterna()];
          const idx = colaActualizada.findIndex((it) => it.id === itemActual.id);
          const intentos = idx >= 0 ? (colaActualizada[idx].intentos || 0) + 1 : 1;

          if (idx >= 0) {
            colaActualizada[idx] = {
              ...colaActualizada[idx],
              intentos,
              ultimoIntentoIso: ahoraIso,
              ultimoError: mensajeError
            };
            this.persistirCola(colaActualizada);
          }

          this.ultimoError.set(mensajeError);

          this.registrarTrazaCola(
            'ERROR_REINTENTO',
            `Intento ${intentos} pendiente de confirmación`,
            mensajeError
          );

          if (typeof navigator !== 'undefined' && !navigator.onLine) {
            this.enLinea.set(false);
            this.limpiarTimersReintento();
          } else {
            this.programarReintentoConBackoff(intentos);
          }
          break;
        }
      }
    } finally {
      this.procesando = false;
      this.sincronizando.set(false);
    }
  }

  /**
   * Ejecuta las operaciones de un item en orden lógico:
   * primero sube cualquier documento firmado o versión pendiente a AWS S3,
   * actualiza los enlaces canónicos y luego confirma cada operación en PostgreSQL.
   */
  private async despacharOperacionesItemConConfirmacionParcial(itemId: string): Promise<void> {
    const obtenerItemActual = (): ItemColaSplitsSincronizacion | undefined =>
      this.colaInterna().find((it) => it.id === itemId);

    const actualizarOperacionesItem = (mutador: (op: OperacionesSincronizacionSplits) => void): void => {
      const cola = [...this.colaInterna()];
      const idx = cola.findIndex((it) => it.id === itemId);
      if (idx < 0) return;
      const opClon = this.clonarProfundo(cola[idx].operaciones);
      mutador(opClon);
      cola[idx] = {
        ...cola[idx],
        actualizadoEnIso: new Date().toISOString(),
        operaciones: opClon
      };
      this.persistirCola(cola);
    };

    const mapaUrlsResueltasPorColaId = new Map<string, { urlCompleta: string; storageKey: string }>();

    // 1. Upsert de Contratos Split Sheet completos (subiendo primero archivos firmados pendientes a S3)
    const item1 = obtenerItemActual();
    for (const [contratoId, agregadoOriginal] of Object.entries(item1?.operaciones.contratosUpsert || {})) {
      const agregado = this.clonarProfundo(agregadoOriginal);
      const af = agregado.contrato.archivoFirmado;

      if (af && (af.pendienteSubidaS3 || (!af.url && af.dataBase64))) {
        const subido = await this.resolverSubidaS3PendientePorId(
          af.idArchivoCola,
          af.dataBase64,
          contratoId,
          af.nombreArchivo,
          'mml-splits/firmados'
        );
        if (subido) {
          if (af.idArchivoCola) {
            mapaUrlsResueltasPorColaId.set(af.idArchivoCola, subido);
          }
          af.url = subido.urlCompleta;
          af.storageKey = subido.storageKey;
          af.pendienteSubidaS3 = false;
          delete af.dataBase64;
        }
      } else if (af && af.url && af.url.startsWith('http')) {
        // Evitar enviar megabytes de base64 redundante a PostgreSQL cuando ya está en S3
        delete af.dataBase64;
      }

      if (Array.isArray(agregado.contrato.archivosFirmadosHistorico)) {
        for (const h of agregado.contrato.archivosFirmadosHistorico) {
          if (h.idArchivoCola && mapaUrlsResueltasPorColaId.has(h.idArchivoCola)) {
            const subido = mapaUrlsResueltasPorColaId.get(h.idArchivoCola)!;
            h.url = subido.urlCompleta;
            h.storageKey = subido.storageKey;
            h.pendienteSubidaS3 = false;
          }
          if (h.url && h.url.startsWith('http')) {
            delete h.dataBase64;
          }
        }
      }

      if (Array.isArray(agregado.eventosTrazabilidad)) {
        for (const ev of agregado.eventosTrazabilidad) {
          const idColaEv = ev.metadata?.['idArchivoCola'];
          if (idColaEv && mapaUrlsResueltasPorColaId.has(idColaEv)) {
            const subido = mapaUrlsResueltasPorColaId.get(idColaEv)!;
            ev.metadata = {
              ...ev.metadata,
              archivoUrl: subido.urlCompleta,
              storageKey: subido.storageKey,
              pendienteSubidaS3: false
            };
          }
        }
      }

      const resp = await firstValueFrom(
        this.http
          .post<{ success?: boolean; message?: string }>(`${this.apiUrl}/contratos`, agregado)
          .pipe(timeout(REQUEST_TIMEOUT_MS))
      );
      if (!resp || resp.success === false) {
        throw new Error(resp?.message || `El servidor no confirmó el guardado del Split Sheet ${contratoId}.`);
      }
      actualizarOperacionesItem((op) => {
        delete op.contratosUpsert[contratoId];
      });
    }

    // 2. Eliminación lógica de Contratos
    const item2 = obtenerItemActual();
    for (const contratoId of [...(item2?.operaciones.contratosEliminarIds || [])]) {
      const resp = await firstValueFrom(
        this.http
          .delete<{ success?: boolean; message?: string }>(
            `${this.apiUrl}/contratos/${encodeURIComponent(contratoId)}`
          )
          .pipe(timeout(REQUEST_TIMEOUT_MS))
      );
      if (!resp || resp.success === false) {
        throw new Error(resp?.message || `No se pudo archivar el Split Sheet ${contratoId}.`);
      }
      actualizarOperacionesItem((op) => {
        op.contratosEliminarIds = op.contratosEliminarIds.filter((id) => id !== contratoId);
      });
    }

    // 3. Restauración de Contratos
    const item3 = obtenerItemActual();
    for (const contratoId of [...(item3?.operaciones.contratosRestaurarIds || [])]) {
      const resp = await firstValueFrom(
        this.http
          .post<{ success?: boolean; message?: string }>(
            `${this.apiUrl}/contratos/${encodeURIComponent(contratoId)}/restaurar`,
            {}
          )
          .pipe(timeout(REQUEST_TIMEOUT_MS))
      );
      if (!resp || resp.success === false) {
        throw new Error(resp?.message || `No se pudo restaurar el Split Sheet ${contratoId}.`);
      }
      actualizarOperacionesItem((op) => {
        op.contratosRestaurarIds = op.contratosRestaurarIds.filter((id) => id !== contratoId);
      });
    }

    // 4. Snapshots de versiones (subiendo primero su PDF a S3 si estaba en cola offline)
    const item4 = obtenerItemActual();
    for (const versionOriginal of [...(item4?.operaciones.versionesCrear || [])]) {
      const version = this.clonarProfundo(versionOriginal);
      if (version.pendienteSubidaS3 && version.idArchivoCola) {
        const subido = await this.resolverSubidaS3PendientePorId(
          version.idArchivoCola,
          undefined,
          version.contratoId,
          version.nombreArchivo,
          'mml-splits/versiones'
        );
        if (subido) {
          mapaUrlsResueltasPorColaId.set(version.idArchivoCola, subido);
          version.archivoUrl = subido.urlCompleta;
          version.storageKey = subido.storageKey;
          version.pendienteSubidaS3 = false;
        }
      }

      const resp = await firstValueFrom(
        this.http
          .post<{ success?: boolean; message?: string }>(`${this.apiUrl}/versiones`, {
            id: version.id,
            contratoId: version.contratoId,
            markdown: version.markdown,
            autor: version.autor,
            motivo: version.motivo,
            archivoUrl: version.archivoUrl,
            storageKey: version.storageKey,
            nombreArchivo: version.nombreArchivo,
            tamanoBytes: version.tamanoBytes,
            cambios: version.cambios,
            creadoEn: version.creadoEn
          })
          .pipe(timeout(REQUEST_TIMEOUT_MS))
      );
      if (!resp || resp.success === false) {
        throw new Error(resp?.message || `No se pudo registrar versión del Split Sheet ${version.contratoId}.`);
      }
      actualizarOperacionesItem((op) => {
        op.versionesCrear = op.versionesCrear.filter((v) => v.id !== version.id);
      });
    }

    // 5. Eventos de trazabilidad
    const item5 = obtenerItemActual();
    for (const eventoOriginal of [...(item5?.operaciones.eventosTrazabilidad || [])]) {
      const evento = this.clonarProfundo(eventoOriginal);
      const idColaEv = evento.metadata?.['idArchivoCola'];
      if (idColaEv && mapaUrlsResueltasPorColaId.has(idColaEv)) {
        const subido = mapaUrlsResueltasPorColaId.get(idColaEv)!;
        evento.metadata = {
          ...evento.metadata,
          archivoUrl: subido.urlCompleta,
          storageKey: subido.storageKey,
          pendienteSubidaS3: false
        };
      }

      const resp = await firstValueFrom(
        this.http
          .post<{ success?: boolean; message?: string }>(`${this.apiUrl}/trazabilidad`, evento)
          .pipe(timeout(REQUEST_TIMEOUT_MS))
      );
      if (!resp || resp.success === false) {
        throw new Error(resp?.message || `No se pudo registrar evento de trazabilidad ${evento.id}.`);
      }
      actualizarOperacionesItem((op) => {
        op.eventosTrazabilidad = op.eventosTrazabilidad.filter((e) => e.id !== evento.id);
      });
    }

    // 6. Upsert de Directorio de Participantes (en bloque)
    const item6 = obtenerItemActual();
    const listaDirectorio = Object.values(item6?.operaciones.directorioUpsert || {});
    if (listaDirectorio.length > 0) {
      const resp = await firstValueFrom(
        this.http
          .post<{ success?: boolean; message?: string }>(`${this.apiUrl}/directorio/upsert`, {
            items: listaDirectorio
          })
          .pipe(timeout(REQUEST_TIMEOUT_MS))
      );
      if (!resp || resp.success === false) {
        throw new Error(resp?.message || 'No se pudo sincronizar el directorio de participantes.');
      }
      actualizarOperacionesItem((op) => {
        op.directorioUpsert = {};
      });
    }

    // 7. Eliminación en Directorio de Participantes
    const item7 = obtenerItemActual();
    for (const docNorm of [...(item7?.operaciones.directorioEliminarIds || [])]) {
      const resp = await firstValueFrom(
        this.http
          .delete<{ success?: boolean; message?: string }>(
            `${this.apiUrl}/directorio/${encodeURIComponent(docNorm)}`
          )
          .pipe(timeout(REQUEST_TIMEOUT_MS))
      );
      if (!resp || resp.success === false) {
        throw new Error(resp?.message || `No se pudo retirar el participante ${docNorm} del directorio.`);
      }
      actualizarOperacionesItem((op) => {
        op.directorioEliminarIds = op.directorioEliminarIds.filter((d) => d !== docNorm);
      });
    }
  }

  private programarReintentoConBackoff(intentos: number): void {
    this.limpiarTimersReintento();

    const exp = Math.min(intentos - 1, 4);
    const delayBase = Math.min(BASE_RETRY_MS * Math.pow(2, exp), MAX_RETRY_MS);
    const jitter = Math.floor(Math.random() * 1000);
    const delayMs = Math.min(delayBase + jitter, MAX_RETRY_MS);

    let segundosRestantes = Math.max(1, Math.ceil(delayMs / 1000));
    this.segundosParaReintento.set(segundosRestantes);

    this.countdownIntervalId = setInterval(() => {
      segundosRestantes = Math.max(0, segundosRestantes - 1);
      this.segundosParaReintento.set(segundosRestantes);
      if (segundosRestantes <= 0 && this.countdownIntervalId) {
        clearInterval(this.countdownIntervalId);
        this.countdownIntervalId = null;
      }
    }, 1000);

    this.retryTimeoutId = setTimeout(() => {
      this.limpiarTimersReintento();
      void this.procesarCola(false);
    }, delayMs);
  }

  private limpiarTimersReintento(): void {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }
    if (this.countdownIntervalId) {
      clearInterval(this.countdownIntervalId);
      this.countdownIntervalId = null;
    }
    this.segundosParaReintento.set(0);
  }

  private formatearErrorHttp(err: unknown): string {
    if (!err) return 'No se pudieron subir los cambios en este momento. Se reintentará automáticamente.';
    if (typeof err === 'object' && err !== null && (err as { name?: string }).name === 'TimeoutError') {
      return 'La conexión está tardando más de lo normal. Tus cambios siguen seguros en cola y se reintentará automáticamente.';
    }
    if (err instanceof HttpErrorResponse) {
      if (err.status === 0) {
        return 'Sin conexión en este momento. Tus cambios siguen guardados en cola y se subirán automáticamente al conectar.';
      }
      if (err.status === 401 || err.status === 403) {
        return 'Tu sesión expiró o no tienes permisos suficientes. Inicia sesión nuevamente; tus cambios en cola se conservarán.';
      }
      if (err.status === 413) {
        return 'El archivo firmado adjunto supera el tamaño máximo permitido.';
      }
      const msgServer = err.error?.message || err.error?.error;
      return msgServer || 'No se pudieron subir los cambios en este momento. Se reintentará automáticamente.';
    }
    if (err instanceof Error) {
      return err.message;
    }
    return String(err);
  }

  private construirDetalleOperacionesItem(op: OperacionesSincronizacionSplits): string[] {
    const detalles: string[] = [];

    for (const [id, c] of Object.entries(op.contratosUpsert || {})) {
      const titulo = c.contrato.tituloObra?.trim() || 'Obra sin título';
      const codigo = c.contrato.codigoSesion || id;
      const cantColabs = (c.colaboradores || []).filter((col) => !col.eliminado).length;
      const tieneFirmado = c.contrato.estado === 'CON_FIRMAS' || !!c.contrato.archivoFirmado;
      detalles.push(
        `Split Sheet "${titulo}" (${codigo} · ${cantColabs} participante(s)${tieneFirmado ? ' · Firmado' : ''})`
      );
    }

    for (const id of op.contratosEliminarIds || []) {
      detalles.push(`Archivar acuerdo (${id})`);
    }

    for (const id of op.contratosRestaurarIds || []) {
      detalles.push(`Restaurar acuerdo (${id})`);
    }

    for (const _v of op.versionesCrear || []) {
      detalles.push(`Versión de respaldo del PDF`);
    }

    const cantEventos = op.eventosTrazabilidad?.length || 0;
    if (cantEventos > 0) {
      detalles.push(`Actualización de historial (${cantEventos})`);
    }

    const cantDir = Object.keys(op.directorioUpsert || {}).length;
    if (cantDir > 0) {
      detalles.push(`${cantDir} ${cantDir === 1 ? 'participante del directorio' : 'participantes del directorio'}`);
    }

    const cantDirDel = op.directorioEliminarIds?.length || 0;
    if (cantDirDel > 0) {
      detalles.push(`Retirar ${cantDirDel} participante(s) del directorio`);
    }

    return detalles.length > 0 ? detalles : ['Cambios del Split Sheet'];
  }

  private construirEtiquetaItem(op: OperacionesSincronizacionSplits): string {
    const detalles = this.construirDetalleOperacionesItem(op);
    return detalles.join(' · ');
  }

  private clonarProfundo<T>(valor: T): T {
    return JSON.parse(JSON.stringify(valor)) as T;
  }
}
